import { DropdownFieldWrapper } from "@app/shared/components/DropdownFieldWrapper/DropdownFieldWrapper";
import {
    getFlagByLocale,
    RESOURCE_NAME as LOCALES_RESOURCE_NAME,
    SupportedLocales,
} from "@app/shared/utils/locales";
import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Icon } from "@design-system/Icon";
import { Textfield } from "@design-system/Inputs/Textfield";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Modal } from "@design-system/Modal";
import { Text } from "@design-system/Typography/Text";
import {
    type ChangeEvent,
    type Dispatch,
    type FC,
    type SetStateAction,
    useEffect,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useUpdateQuestionnaire } from "../../data";
import { DateInputDropdown } from "../../DateInputDropdown";
import { useQuestionnaireDetails } from "../../externalQuestionnaire/data";
import { DeleteConfirmationModalContent } from "./DeleteConfirmationModalContent";
import { useEditOrDeleteDropdownTranslations } from "./i18n";

export const EditQuestionnaireModal: FC<{
    questionnaireId?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}> = ({ questionnaireId, open, onOpenChange }) => {
    const { t, keys } = useEditOrDeleteDropdownTranslations();
    const { t: tLocales } = useTranslation(LOCALES_RESOURCE_NAME);
    const { questionnaire, isPending } = questionnaireId
        ? useQuestionnaireDetails(questionnaireId, {
              enabled: open,
          })
        : { questionnaire: null, isPending: false };

    const { updateAsync } = useUpdateQuestionnaire();

    const [name, setName] = useState<string | undefined>(questionnaire?.name);
    const [email, setEmail] = useState<string | undefined>(
        questionnaire?.requesterEmail ?? undefined,
    );
    const [companyName, setCompanyName] = useState<string | undefined>(
        questionnaire?.companyName ?? undefined,
    );
    const [deadline, setDeadline] = useState<Date | undefined>(
        questionnaire?.deadline ?? undefined,
    );
    const [defaultLocale, setDefaultLocale] = useState<string>(
        questionnaire?.locale?.default ?? SupportedLocales.FR,
    );
    const handleDelete = () => {
        setCurrModal("delete");
    };

    useEffect(() => {
        setName(questionnaire?.name);
        setEmail(questionnaire?.requesterEmail ?? undefined);
        setCompanyName(questionnaire?.companyName ?? undefined);
        setDeadline(questionnaire?.deadline ?? undefined);
        setDefaultLocale(
            (questionnaire?.locale?.default as SupportedLocales) ??
                SupportedLocales.FR,
        );
    }, [questionnaire]);

    const makeEventHandler =
        (setter: Dispatch<SetStateAction<string | undefined>>) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            setter(e.target.value);
        };

    const canConfirm = !!name && !!defaultLocale;

    const handleConfirm = async () => {
        if (!canConfirm || !questionnaireId) return;

        await updateAsync({
            id: questionnaireId,
            input: {
                name,
                requesterEmail: email,
                companyName,
                deadline,
                locale: {
                    availableLocales: [defaultLocale],
                    default: defaultLocale,
                },
            },
        });
    };

    const [currModal, setCurrModal] = useState<"edit" | "delete">("edit");

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setTimeout(() => setCurrModal("edit"), 200);
        }
        onOpenChange?.(open);
    };

    const isLocale = (value: string): value is SupportedLocales => {
        return Object.values(SupportedLocales).includes(
            value as SupportedLocales,
        );
    };
    const getLocalFlag = (locale: string) => {
        if (isLocale(locale)) {
            return getFlagByLocale(locale);
        }
        return getFlagByLocale(defaultLocale as SupportedLocales);
    };

    const handleLocaleChange = (locale: string[]) => {
        const newLocale = locale[0];
        setDefaultLocale(newLocale);
    };

    const localFlag = getLocalFlag(defaultLocale);

    return (
        <Modal open={open} onOpenChange={handleOpenChange}>
            <Modal.Overlay centerContent>
                {currModal === "edit" ? (
                    <Modal.Content className="max-w-[600px]">
                        <Modal.Header withClose>
                            <Modal.Title>
                                {t(keys.edit_questionnaire_modal_title)}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <FlexCol gap="6">
                                <FlexCol gap="4">
                                    <Textfield
                                        className="h-6 !text-neutral-700"
                                        value={name}
                                        onChange={makeEventHandler(setName)}
                                        placeholder={t(
                                            keys.edit_questionnaire_modal_name_placeholder,
                                        )}
                                        required
                                    />

                                    <DateInputDropdown
                                        value={
                                            deadline
                                                ? new Date(deadline)
                                                : undefined
                                        }
                                        onChange={setDeadline}
                                    />
                                </FlexCol>
                                <FlexCol gap="1">
                                    <Dropdown
                                        value={[defaultLocale]}
                                        onValueChange={handleLocaleChange}
                                    >
                                        <Dropdown.Trigger
                                            className="w-[204px]"
                                            disabled
                                        >
                                            <DropdownFieldWrapper
                                                placeholder={t(
                                                    keys.edit_questionnaire_modal_locale_placeholder,
                                                )}
                                                placeholderUp={!!defaultLocale}
                                                required
                                                leadingElement={
                                                    <Icon name={localFlag} />
                                                }
                                                trailingElement={
                                                    <Icon
                                                        name="chevronDown"
                                                        className="text-neutral-300"
                                                    />
                                                }
                                            >
                                                <Text>
                                                    {tLocales(defaultLocale)}
                                                </Text>
                                            </DropdownFieldWrapper>
                                        </Dropdown.Trigger>
                                    </Dropdown>
                                    <Text variant="body" color="secondary">
                                        {t(
                                            keys.edit_questionnaire_modal_language_warning,
                                        )}
                                    </Text>
                                </FlexCol>
                                <FlexCol gap="2">
                                    <FlexCol gap="1">
                                        <Text
                                            variant="body-lg-bold"
                                            color="primary"
                                        >
                                            {t(
                                                keys.edit_questionnaire_modal_requested_by,
                                            )}
                                        </Text>
                                        <Text variant="body" color="secondary">
                                            {t(
                                                keys.edit_questionnaire_modal_requested_by_description,
                                            )}
                                        </Text>
                                    </FlexCol>
                                    <FlexCol gap="4">
                                        <Textfield
                                            className="h-6"
                                            value={companyName}
                                            onChange={makeEventHandler(
                                                setCompanyName,
                                            )}
                                            placeholder={t(
                                                keys.edit_questionnaire_modal_company_name_placeholder,
                                            )}
                                        />
                                        <Textfield
                                            className="h-6"
                                            value={email}
                                            onChange={makeEventHandler(
                                                setEmail,
                                            )}
                                            placeholder={t(
                                                keys.edit_questionnaire_modal_requester_email_placeholder,
                                            )}
                                        />
                                    </FlexCol>
                                </FlexCol>

                                <FlexRow
                                    className="cursor-pointer"
                                    onClick={handleDelete}
                                    alignItems="center"
                                    gap="4"
                                >
                                    <Icon
                                        name="trash"
                                        className="text-error-500"
                                        size="md"
                                    />
                                    <FlexCol alignItems="start">
                                        <Text
                                            variant="body-bold"
                                            color="primary"
                                        >
                                            {t(
                                                keys.edit_questionnaire_modal_delete_questionnaire_label,
                                            )}
                                        </Text>
                                        <Text variant="body" color="primary">
                                            {t(
                                                keys.edit_questionnaire_modal_delete_questionnaire_description,
                                            )}
                                        </Text>
                                    </FlexCol>
                                </FlexRow>
                            </FlexCol>
                        </Modal.Body>
                        <Modal.Footer>
                            <Modal.CancelCTA>
                                {t(keys.edit_questionnaire_modal_cancel_label)}
                            </Modal.CancelCTA>
                            <Tooltip>
                                <Tooltip.Trigger asChild>
                                    {/* This Box below is necessary because the tooltip doesn't open when the button is
                                     * disabled but we want to display the tooltip just when the button is disabled
                                     */}
                                    <Box className="w-fit self-end">
                                        <Modal.ConfirmCTA
                                            onClick={handleConfirm}
                                            disabled={!canConfirm}
                                            loading={isPending}
                                        >
                                            {t(
                                                keys.edit_questionnaire_modal_confirm_label,
                                            )}
                                        </Modal.ConfirmCTA>
                                    </Box>
                                </Tooltip.Trigger>
                                {!canConfirm && (
                                    <Tooltip.Content>
                                        {t(
                                            keys.edit_questionnaire_modal_tooltip_no_name,
                                        )}
                                    </Tooltip.Content>
                                )}
                            </Tooltip>
                        </Modal.Footer>
                    </Modal.Content>
                ) : (
                    <DeleteConfirmationModalContent
                        questionnaireId={questionnaireId}
                    />
                )}
            </Modal.Overlay>
        </Modal>
    );
};
